'use client'

import { useEffect, useState } from 'react'
import {
    deletePlan,
    formatPlanPrice,
    getPlans,
    Plan,
    RECURRENCE_LABELS,
} from '@/lib/api/plans/plan-api'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
    MdAdd,
    MdArrowForward,
    MdAttachMoney,
    MdDelete,
    MdEdit,
    MdLock,
    MdStar,
    MdViewList,
} from 'react-icons/md'

const getCardGradient = (index: number): string => {
    const gradients = [
        'from-secondary to-secondary/70',
        'from-secondary to-secondary/70',
        'from-accent to-accent/70',
        'from-info to-info/70',
        'from-success to-success/70',
        'from-warning to-warning/70',
        'from-error to-error/70',
    ]
    return gradients[index % gradients.length]
}

const countFeatures = (features: any): number => {
    if (!features) return 0
    if (typeof features === 'object') return Object.keys(features).length
    return 0
}

export default function PlansPage() {
    const navigate = useNavigate()
    const [plans, setPlans] = useState<Plan[]>([])
    const [loading, setLoading] = useState(true)
    const [deleteModalId, setDeleteModalId] = useState<number | null>(null)

    useEffect(() => {
        loadPlans()
    }, [])

    const loadPlans = async () => {
        setLoading(true)
        try {
            const response = await getPlans(true)
            if (response.success) {
                setPlans(response.result || [])
            } else {
                toast.error(response.error || 'Erro ao carregar planos')
            }
        } catch {
            toast.error('Erro ao carregar planos')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async () => {
        if (!deleteModalId) return
        try {
            const response = await deletePlan(deleteModalId)
            if (response.success) {
                setPlans(prev => prev.filter(p => p.id !== deleteModalId))
                toast.success('Plano removido com sucesso')
            } else {
                toast.error(response.error || 'Erro ao remover plano')
            }
        } catch {
            toast.error('Erro ao remover plano')
        } finally {
            setDeleteModalId(null)
        }
    }

    if (loading && plans.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando planos...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-secondary via-secondary/90 to-secondary/70 flex items-center justify-center shadow-lg shadow-secondary/20">
                        <MdViewList className="text-3xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-base-content">
                            Planos
                        </h1>
                        <p className="text-sm md:text-base text-base-content/60 mt-1">
                            {plans.length}{' '}
                            {plans.length === 1
                                ? 'plano cadastrado'
                                : 'planos cadastrados'}
                        </p>
                    </div>
                </div>
                <button
                    onClick={() => navigate('/plans/new')}
                    className="btn btn-secondary gap-2 shadow-lg shadow-secondary/20"
                >
                    <MdAdd className="text-lg" />
                    Novo Plano
                </button>
            </div>

            {plans.length === 0 ? (
                <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-2xl border border-base-300/50 shadow-lg p-16 text-center">
                    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-secondary/10 to-secondary/5 flex items-center justify-center">
                        <MdViewList className="text-5xl text-secondary/40" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-base-content mb-3">
                        Nenhum plano cadastrado
                    </h3>
                    <p className="text-base-content/60 mb-8 max-w-md mx-auto">
                        Comece criando o primeiro plano para oferecer aos seus
                        clientes.
                    </p>
                    <button
                        onClick={() => navigate('/plans/new')}
                        className="btn btn-secondary btn-lg gap-2 shadow-lg shadow-secondary/20"
                    >
                        <MdAdd className="text-xl" />
                        Criar Primeiro Plano
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
                    {plans.map((plan, index) => (
                        <div
                            key={plan.id}
                            onClick={() => navigate(`/plans/${plan.id}`)}
                            className="group bg-gradient-to-br from-base-100 to-base-200/20 rounded-2xl border border-base-300/50 shadow-sm hover:shadow-xl hover:border-secondary/30 transition-all duration-300 cursor-pointer overflow-hidden relative"
                        >
                            <div className="absolute inset-0 bg-gradient-to-br from-secondary/0 to-secondary/0 group-hover:from-secondary/5 group-hover:to-transparent transition-all duration-300 pointer-events-none" />

                            <div className="relative p-6">
                                {/* Header */}
                                <div className="flex items-start gap-4 mb-4">
                                    <div
                                        className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${getCardGradient(index)} flex items-center justify-center shadow-lg flex-shrink-0`}
                                    >
                                        <MdAttachMoney className="text-2xl text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-lg font-bold text-base-content group-hover:text-secondary transition-colors line-clamp-1">
                                            {plan.name}
                                        </h3>
                                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                                            <span className="badge badge-sm badge-secondary badge-outline">
                                                {RECURRENCE_LABELS[plan.recurrence]}
                                            </span>
                                            {plan.private && (
                                                <span className="badge badge-sm badge-warning gap-1">
                                                    <MdLock className="text-xs" />
                                                    Privado
                                                </span>
                                            )}
                                            {plan.isCustom && (
                                                <span className="badge badge-sm badge-secondary gap-1">
                                                    <MdStar className="text-xs" />
                                                    Sob Medida
                                                </span>
                                            )}
                                            {plan.tier && (
                                                <span className="badge badge-sm badge-info badge-outline">
                                                    {plan.tier.name}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex-shrink-0">
                                        <div className="w-8 h-8 rounded-lg bg-base-200/50 group-hover:bg-secondary/10 flex items-center justify-center transition-colors">
                                            <MdArrowForward className="text-base-content/40 group-hover:text-secondary transition-colors" />
                                        </div>
                                    </div>
                                </div>

                                {/* Descrição */}
                                <p className="text-sm text-base-content/60 line-clamp-2 mb-4">
                                    {plan.description}
                                </p>

                                {/* Preço */}
                                <div className="flex items-baseline gap-2 mb-4 px-4 py-3 bg-base-200/30 rounded-xl">
                                    <span className="text-2xl font-bold text-secondary">
                                        {formatPlanPrice(plan.price)}
                                    </span>
                                    <span className="text-sm text-base-content/50">
                                        /{RECURRENCE_LABELS[plan.recurrence].toLowerCase().slice(0, 3)}
                                    </span>
                                    {plan.isCustom &&
                                        plan.customPrice != null && (
                                            <span className="text-xs text-base-content/40 ml-auto">
                                                Sob medida:{' '}
                                                {formatPlanPrice(plan.customPrice)}
                                            </span>
                                        )}
                                </div>

                                {/* Features count */}
                                {countFeatures(plan.features) > 0 && (
                                    <div className="text-xs text-base-content/50 mb-4">
                                        {countFeatures(plan.features)}{' '}
                                        {countFeatures(plan.features) === 1
                                            ? 'recurso'
                                            : 'recursos'}{' '}
                                        incluídos
                                    </div>
                                )}

                                {/* Footer */}
                                <div className="pt-4 border-t border-base-300/50">
                                    <div className="flex items-center justify-between gap-4">
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-xs"
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    navigate(
                                                        `/plans/edit/${plan.id}`,
                                                    )
                                                }}
                                                title="Editar"
                                            >
                                                <MdEdit className="text-base" />
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-xs text-error"
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    setDeleteModalId(plan.id)
                                                }}
                                                title="Remover"
                                            >
                                                <MdDelete className="text-base" />
                                            </button>
                                        </div>
                                        <div className="text-xs text-base-content/50">
                                            {format(
                                                new Date(plan.createdAt),
                                                'dd/MM/yyyy',
                                                { locale: ptBR },
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal de confirmação de exclusão */}
            {deleteModalId && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg">
                            Confirmar Exclusão
                        </h3>
                        <p className="py-4 text-base-content/70">
                            Tem certeza que deseja remover este plano? As
                            assinaturas existentes não serão afetadas, mas
                            novas assinaturas não poderão ser criadas.
                        </p>
                        <div className="modal-action">
                            <button
                                className="btn btn-ghost"
                                onClick={() => setDeleteModalId(null)}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-error"
                                onClick={handleDelete}
                            >
                                Remover
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={() => setDeleteModalId(null)}
                    />
                </div>
            )}
        </div>
    )
}
